import React, { useEffect, useState, useMemo, useCallback } from "react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
    Box,
    Typography,
    Button,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    FormControlLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel, Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import InputAdornment from "@mui/material/InputAdornment";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useParams, useNavigate } from "react-router-dom";
import TimingEventSubNav from "./TimingEventSubNav";
import SwissSkiUpdateDialog from "./SwissSkiUpdateDialog";
import TeamCheckAdmin from "./TeamCheckAdmin";
import GroupsIcon from "@mui/icons-material/Groups";
import { authHeader, clearToken } from "../../auth";

// Ordre d'affichage préféré pour les champs connus — tout champ
// personnalisé additionnel (ajouté à l'import) apparaît après, dans
// l'ordre où il est rencontré.
const FIXED_ORDER = [
    "bib",
    "transponder",
    "fiscode",
    "lastname",
    "firstname",
    "gender",
    "birthyear",
    "category",
    "nation",
    "club",
    "fromlist",
    "points",
    "association",
];

const COLUMN_LABELS = {
    bib: "Dossard",
    transponder: "Transpondeur",
    fiscode: "Code",
    lastname: "Nom",
    firstname: "Prénom",
    gender: "Genre",
    birthyear: "Année",
    category: "Catégorie",
    nation: "Nation",
    club: "Club",
    fromlist: "From list",
    points: "Points",
    association: "Association",
};

// Libellés discipline pour le titre du PDF — mêmes valeurs que
// TimingSwissSkiExport.jsx/TimingStartList.jsx, dupliqué ici pour ne pas
// créer de dépendance entre les pages.
const DISCIPLINE_LABELS = {
    SL: "Slalom",
    GS: "Riesenslalom",
    SG: "Super-G",
    DH: "Downhill / Abfahrt",
    CR: "Combi-Race",
    AC: "Alpine-Kombi",
    PS: "Parallel-Slalom",
};

// Disciplines disponibles dans la liste de points FIS importée (voir
// fisPointsListRouter.js) — utilisé pour choisir QUELLE colonne de points
// remplit le champ générique "points" d'un athlète à l'ajout/rafraîchissement,
// cet écran n'ayant pas connaissance de la discipline de l'event.
const FIS_DISCIPLINES = [
    { value: "dh_points", label: "DH" },
    { value: "sl_points", label: "SL" },
    { value: "gs_points", label: "GS" },
    { value: "sg_points", label: "SG" },
    { value: "ac_points", label: "AC" },
    { value: "cl_points", label: "CL (télémark)" },
    { value: "sp_points", label: "SP (télémark)" },
];

export default function TimingAthletesList() {
    const { eventKey } = useParams();
    const navigate = useNavigate();

    const [athletes, setAthletes] = useState([]);
    const [sortBy, setSortBy] = useState("abc"); // "abc" | "points" | "club" | "field"
    // Champ libre utilisé quand sortBy === "field" — ajouté le 09.09.2026
    // (point C de la demande : trier par un champ quelconque, pas
    // seulement ABC/points/club).
    const [sortField, setSortField] = useState("");
    // Regroupement/saut de page par un champ quelconque — remplace
    // l'ancien booléen "pageBreakByCategory" figé sur "category" (point D
    // de la demande du 09.09.2026) : "" = pas de regroupement, sinon le
    // nom du champ choisi.
    const [groupByField, setGroupByField] = useState("");
    // Recherche dans le tableau (tous les champs) — 07.10.2026.
    const [search, setSearch] = useState("");
    const [error, setError] = useState("");
    const [status, setStatus] = useState("");

    // Édition : une fenêtre modale par concurrent, jamais d'édition
    // directe dans le tableau. `editingIndex` = index réel dans
    // `athletes` en cours d'édition (ou null), `draft` = copie de travail
    // modifiable indépendamment tant que ce n'est pas enregistré.
    const [editingIndex, setEditingIndex] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);

    // Discipline utilisée pour choisir la colonne de points FIS à importer
    // (voir FIS_DISCIPLINES ci-dessus) — ajouté le 09.09.2026.
    const [pointsDiscipline, setPointsDiscipline] = useState("gs_points");

    // Secteur FIS de la course (AL alpin / TM télémark), lu dans la config
    // Course & exports — 07.10.2026 : présélectionne la bonne colonne de
    // points et limite la recherche "Ajouter un athlète" à la bonne liste.
    const [raceSector, setRaceSector] = useState(null);

    // Import de la liste de points FIS (fichier TSV téléchargé par
    // l'opérateur — voir fisPointsListRouter.js pour le pourquoi il n'y a
    // pas de téléchargement automatique).
    const [fisListStatus, setFisListStatus] = useState(null);
    const [fisImporting, setFisImporting] = useState(false);

    // Mise à jour depuis la liste de points SWISS-SKI (fichier d'export
    // Swiss-Ski, lu dans le navigateur) — ajouté le 01.10.2026, voir
    // SwissSkiUpdateDialog.jsx.
    const [showSwissSkiDialog, setShowSwissSkiDialog] = useState(false);

    // Validation des inscriptions par les chefs d'équipe (lien public) —
    // ajouté le 01.10.2026, voir TeamCheckAdmin.jsx / teamCheckRouter.js.
    const [showTeamCheck, setShowTeamCheck] = useState(false);

    // Ajout d'un athlète depuis la liste de points FIS.
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [addSearchQuery, setAddSearchQuery] = useState("");
    const [addSearchResults, setAddSearchResults] = useState([]);
    const [addSearching, setAddSearching] = useState(false);
    // Liste dans laquelle chercher : FIS ou Swiss-Ski (liste Swiss-Ski en
    // base, 07.10.2026) + colonne de points Swiss-Ski utilisée.
    const [addSource, setAddSource] = useState("fis");
    const [swissPointsCol, setSwissPointsCol] = useState("Punkte");

    // Rafraîchissement des points d'un athlète déjà présent, dans la
    // fenêtre d'édition.
    const [refreshingPoints, setRefreshingPoints] = useState(false);
    const [refreshError, setRefreshError] = useState("");

    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            });
            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }
            return res;
        },
        [navigate]
    );

    const load = useCallback(async () => {
        const res = await apiFetch(`/api/timing/athletes/${encodeURIComponent(eventKey)}`);
        if (!res) return;
        if (res.ok) {
            setAthletes(await res.json());
        }
    }, [apiFetch, eventKey]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((cfg) => {
                if (!cfg) return;
                const d = String(cfg.discipline || "").toUpperCase();
                const tm = cfg.sector === "TM" || ["CL", "SP", "PSP", "PSQ"].includes(d);
                setRaceSector(tm ? "TM" : cfg.sector === "AL" || !cfg.sector ? "AL" : null);
                const column = tm
                    ? (d === "CL" ? "cl_points" : ["SP", "PSP", "PS", "PSQ"].includes(d) ? "sp_points" : "cl_points")
                    : { SL: "sl_points", GS: "gs_points", SG: "sg_points", DH: "dh_points", AC: "ac_points", PSL: "sl_points", PGS: "gs_points" }[d];
                if (column) setPointsDiscipline(column);
            })
            .catch(() => {});
    }, [apiFetch, eventKey]);

    // Message d'erreur complet — inclut data.details (le vrai message SQL
    // ou d'exception renvoyé par le serveur) en plus de data.error,
    // au lieu du seul "Erreur serveur" générique peu exploitable tel quel
    // (voir demande du 09.09.2026, point B).
    const formatApiError = (data, res) => {
        const base = data.error || `HTTP ${res.status}`;
        return data.details && data.details !== data.error ? `${base} — ${data.details}` : base;
    };

    const loadFisListStatus = useCallback(async () => {
        const res = await apiFetch("/api/timing/fis-pointslist/status");
        if (!res) return;
        if (res.ok) setFisListStatus(await res.json());
    }, [apiFetch]);

    useEffect(() => {
        loadFisListStatus();
    }, [loadFisListStatus]);

    // Persiste le tableau `athletes` complet (même mécanisme que l'édition
    // existante — l'API attend systématiquement la liste entière).
    const persistAthletes = async (updated) => {
        const res = await apiFetch(`/api/timing/athletes/import/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(updated),
        });
        if (!res) return false;
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(formatApiError(data, res));
            return false;
        }
        setAthletes(updated);
        setError("");
        return true;
    };

    // Colonnes dérivées de ce qui est réellement présent dans les données
    // (champs fixes connus dans un ordre stable, puis tout champ
    // personnalisé additionnel).
    const columns = useMemo(() => {
        const allKeys = new Set();
        athletes.forEach((a) => Object.keys(a).forEach((k) => allKeys.add(k)));
        const fixed = FIXED_ORDER.filter((k) => allKeys.has(k));
        const custom = [...allKeys].filter((k) => !FIXED_ORDER.includes(k));
        return [...fixed, ...custom];
    }, [athletes]);

    // Colonnes affichées à l'écran / imprimées / dans le PDF — ajouté le
    // 09.09.2026. Toutes cochées par défaut ; les nouvelles colonnes qui
    // apparaissent plus tard (ex: nouveau champ importé) sont ajoutées
    // automatiquement, SANS toucher aux désélections déjà faites à la
    // main. La fenêtre d'édition d'un concurrent continue elle d'utiliser
    // `columns` (toutes) — cette restriction ne concerne que
    // l'affichage/impression/PDF.
    const [printColumns, setPrintColumns] = useState(null); // null = pas encore initialisé -> toutes
    useEffect(() => {
        setPrintColumns((prev) => {
            if (prev == null) return columns;
            const kept = prev.filter((c) => columns.includes(c));
            const added = columns.filter((c) => !kept.includes(c));
            return [...kept, ...added];
        });
    }, [columns]);
    const displayColumns = columns.filter((c) => (printColumns || columns).includes(c));
    const [showColumnsDialog, setShowColumnsDialog] = useState(false);

    // Outils de champs (04.11.2026) : mettre un champ entier en MAJUSCULES
    // (ex. nom de famille), en minuscules ou avec une Majuscule Initiale, et
    // copier un champ dans un autre (tous les participants, ou seulement
    // ceux dont le champ cible est vide).
    const [showFieldTools, setShowFieldTools] = useState(false);
    const [ftField, setFtField] = useState("lastname");
    const [ftCase, setFtCase] = useState("upper");
    const [ftFrom, setFtFrom] = useState("lastname");
    const [ftTo, setFtTo] = useState("");
    const [ftOnlyEmpty, setFtOnlyEmpty] = useState(true);
    const titleCase = (v) =>
        String(v).toLocaleLowerCase("fr-CH").replace(/(^|[\s\-'’])(\p{L})/gu, (m, sep, c) => sep + c.toLocaleUpperCase("fr-CH"));
    const applyCase = async () => {
        const conv = (v) => (v == null || v === "" ? v : ftCase === "upper" ? String(v).toLocaleUpperCase("fr-CH") : ftCase === "lower" ? String(v).toLocaleLowerCase("fr-CH") : titleCase(v));
        let n = 0;
        const updated = athletes.map((a) => {
            const v = conv(a[ftField]);
            if (v === a[ftField]) return a;
            n++;
            return { ...a, [ftField]: v };
        });
        if (!n) return setStatus("Aucun changement.");
        if (await persistAthletes(updated)) setStatus(`« ${COLUMN_LABELS[ftField] || ftField} » modifié pour ${n} participant(s) ✓`);
    };
    const applyCopy = async () => {
        const target = ftTo.trim();
        if (!target || target === ftFrom) return setError("Choisis un champ cible différent du champ source.");
        let n = 0;
        const updated = athletes.map((a) => {
            if (ftOnlyEmpty && a[target] != null && String(a[target]).trim() !== "") return a;
            if (a[ftFrom] == null || a[ftFrom] === "") return a;
            n++;
            return { ...a, [target]: a[ftFrom] };
        });
        if (!n) return setStatus("Aucun champ copié.");
        if (
            !ftOnlyEmpty &&
            !window.confirm(`Remplacer « ${COLUMN_LABELS[target] || target} » par « ${COLUMN_LABELS[ftFrom] || ftFrom} » pour ${n} participant(s) ?`)
        )
            return;
        if (await persistAthletes(updated)) setStatus(`« ${COLUMN_LABELS[ftFrom] || ftFrom} » copié dans « ${COLUMN_LABELS[target] || target} » (${n}) ✓`);
    };

    const openEditor = (realIndex) => {
        setEditingIndex(realIndex);
        setDraft({ ...athletes[realIndex] });
        setStatus("");
        setError("");
        setRefreshError("");
    };

    const cancelEditor = () => {
        setEditingIndex(null);
        setDraft(null);
    };

    const updateDraftField = (field, value) => {
        setDraft((prev) => ({ ...prev, [field]: value }));
    };

    const saveEditor = async () => {
        const updated = athletes.map((a, i) => (i === editingIndex ? draft : a));

        setSaving(true);
        setError("");

        const ok = await persistAthletes(updated);

        setSaving(false);
        if (!ok) return;

        setEditingIndex(null);
        setDraft(null);
        setStatus("Concurrent enregistré ✓");
    };

    // Supprime un concurrent de la liste — ajouté le 09.09.2026. Retire
    // l'entrée et persiste immédiatement le tableau résultant (même
    // endpoint que l'édition, qui remplace toujours la liste entière).
    const deleteAthlete = async (realIndex) => {
        const target = athletes[realIndex];
        if (
            !window.confirm(
                `Supprimer ${target.lastname || ""} ${target.firstname || ""} (dossard ${target.bib || "—"}) ` +
                `de la liste des participants ? Cette action ne touche pas les temps déjà enregistrés au chrono.`
            )
        ) {
            return;
        }
        const updated = athletes.filter((_, i) => i !== realIndex);
        const ok = await persistAthletes(updated);
        if (ok) setStatus("Concurrent supprimé ✓");
    };

    // Supprime TOUS les participants de la course (double confirmation) —
    // 07.10.2026. Ne touche ni aux listes de départ ni aux temps.
    const deleteAllAthletes = async () => {
        if (athletes.length === 0) return;
        if (!window.confirm(`Supprimer les ${athletes.length} participants de cette course ?`)) return;
        if (!window.confirm("Vraiment sûr ? La liste des participants sera vide. Action irréversible.")) return;
        const ok = await persistAthletes([]);
        if (ok) setStatus("Tous les participants ont été supprimés ✓");
    };

    // --- Import de la liste de points FIS ------------------------------
    // Convertit un .xlsx en TSV côté navigateur (SheetJS) pour réutiliser
    // TEL QUEL le même parseur backend que pour un fichier .txt/.tsv déjà
    // en tabulations — évite de dupliquer la logique de parsing des deux
    // côtés. Birthdate est un vrai objet Date dans le fichier Excel (donc
    // reformaté ici en YYYY-MM-DD) ; Calculationdate y est déjà stocké
    // comme texte "DD-MM-YYYY", donc passe tel quel.
    function xlsxSheetToTsv(workbook) {
        const ws = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: true });
        if (json.length === 0) return "";
        const headers = Object.keys(json[0]);
        const formatCell = (v) => {
            if (v instanceof Date) {
                const y = v.getFullYear();
                const m = String(v.getMonth() + 1).padStart(2, "0");
                const d = String(v.getDate()).padStart(2, "0");
                return `${y}-${m}-${d}`;
            }
            return v == null ? "" : String(v);
        };
        const lines = [headers.join("\t")];
        json.forEach((row) => {
            lines.push(headers.map((h) => formatCell(row[h])).join("\t"));
        });
        return lines.join("\n");
    }

    const handleFisListFile = async (e) => {
        const file = e.target.files[0];
        e.target.value = ""; // permet de réimporter le même fichier plus tard si besoin
        if (!file) return;

        setFisImporting(true);
        setError("");
        setStatus("");
        try {
            let text;
            if (/\.xlsx?$/i.test(file.name)) {
                const buf = await file.arrayBuffer();
                const workbook = XLSX.read(buf, { type: "array", cellDates: true });
                text = xlsxSheetToTsv(workbook);
            } else {
                text = await file.text();
            }
            if (!text) {
                setError("Fichier vide ou illisible.");
                return;
            }
            const res = await apiFetch("/api/timing/fis-pointslist/import", {
                method: "POST",
                body: JSON.stringify({ tsv: text }),
            });
            if (!res) return;
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(formatApiError(data, res));
                return;
            }
            setStatus(
                `Liste de points importée ✓ — ${data.imported} athlète(s)` +
                (data.skipped ? `, ${data.skipped} ligne(s) ignorée(s)` : "") +
                (data.listName ? ` (${data.listName})` : "")
            );
            loadFisListStatus();
        } finally {
            setFisImporting(false);
        }
    };

    // --- Ajout d'un athlète depuis la liste de points FIS --------------
    const searchFisAthletes = async () => {
        if (addSearchQuery.trim().length < 2) return;
        setAddSearching(true);
        try {
            const q = encodeURIComponent(addSearchQuery.trim());
            const res = await apiFetch(
                addSource === "swissski"
                    ? `/api/timing/swissski-list/search?q=${q}`
                    : `/api/timing/fis-pointslist/search?q=${q}${raceSector ? `&sector=${raceSector}` : ""}`
            );
            if (!res) return;
            if (res.ok) setAddSearchResults(await res.json());
            else setAddSearchResults([]);
        } finally {
            setAddSearching(false);
        }
    };

    // Ligne de la liste Swiss-Ski (format du fichier) -> participant.
    const importAthleteFromSwissSki = async (r) => {
        const code = String(r.FISCode || r.PNr || "").trim();
        const alreadyPresent = athletes.some(
            (a) => a.fiscode && [String(r.PNr), String(r.FISCode)].includes(String(a.fiscode))
        );
        if (alreadyPresent && !window.confirm("Cet athlète est déjà présent dans la liste — l'ajouter quand même ?")) return;
        const p = parseFloat(String(r[swissPointsCol] ?? "").replace(",", "."));
        const newAthlete = {
            bib: "",
            fiscode: code,
            lastname: r.Nachname,
            firstname: r.Vorname,
            gender: r.Gender || "",
            birthyear: r.JG || "",
            nation: r.Nat || "SUI",
            club: r.Clubname || "",
            association: r.RV || "",
            category: r.Kat || "",
            fromlist: `Swiss-Ski ${r._listName || ""}`.trim(),
            points: !isNaN(p) && p < 999 ? p : "",
        };
        const ok = await persistAthletes([...athletes, newAthlete]);
        if (ok) {
            setStatus(`${r.Nachname} ${r.Vorname} ajouté ✓`);
            setShowAddDialog(false);
            setAddSearchQuery("");
            setAddSearchResults([]);
        }
    };

    const importAthleteFromFis = async (fisRow) => {
        const alreadyPresent = athletes.some((a) => a.fiscode && String(a.fiscode) === String(fisRow.fiscode));
        if (alreadyPresent && !window.confirm("Ce fiscode est déjà présent dans la liste — l'ajouter quand même ?")) {
            return;
        }
        const newAthlete = {
            bib: "",
            fiscode: fisRow.fiscode,
            lastname: fisRow.lastname,
            firstname: fisRow.firstname,
            gender: fisRow.gender,
            birthyear: fisRow.birthyear,
            nation: fisRow.nation,
            club: fisRow.club || "",
            fromlist: fisRow.list_id || "",
            points: fisRow[pointsDiscipline] != null && fisRow[pointsDiscipline] < 999 ? fisRow[pointsDiscipline] : "",
        };
        const updated = [...athletes, newAthlete];
        const ok = await persistAthletes(updated);
        if (ok) {
            setStatus(`${fisRow.lastname} ${fisRow.firstname} ajouté ✓`);
            setShowAddDialog(false);
            setAddSearchQuery("");
            setAddSearchResults([]);
        }
    };

    // --- Rafraîchir les points d'un athlète en cours d'édition ---------
    const refreshPointsInEditor = async () => {
        if (!draft?.fiscode) {
            setRefreshError("Ce concurrent n'a pas de fiscode renseigné.");
            return;
        }
        setRefreshingPoints(true);
        setRefreshError("");
        try {
            const res = await apiFetch(`/api/timing/fis-pointslist/by-fiscode/${encodeURIComponent(draft.fiscode)}`);
            if (!res) return;
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setRefreshError(formatApiError(data, res));
                return;
            }
            setDraft((prev) => ({
                ...prev,
                lastname: data.lastname || prev.lastname,
                firstname: data.firstname || prev.firstname,
                nation: data.nation || prev.nation,
                club: data.club || prev.club,
                birthyear: data.birthyear || prev.birthyear,
                fromlist: data.list_id || prev.fromlist,
                points: data[pointsDiscipline] != null && data[pointsDiscipline] < 999 ? data[pointsDiscipline] : prev.points,
            }));
        } finally {
            setRefreshingPoints(false);
        }
    };

    // --- Mise à jour groupée de tous les participants (par fiscode) ----
    // Ajouté le 09.09.2026. Une seule requête bulk-lookup, puis fusion
    // locale selon les champs cochés — seuls les participants qui ont un
    // fiscode ET sont trouvés dans la liste importée sont modifiés.
    const BULK_FIELDS = [
        { key: "points", label: "Points" },
        { key: "fromlist", label: "Liste" },
        { key: "lastname", label: "Nom" },
        { key: "firstname", label: "Prénom" },
        { key: "nation", label: "Nation" },
        { key: "club", label: "Club" },
        { key: "birthyear", label: "Année de naissance" },
    ];
    const [showBulkDialog, setShowBulkDialog] = useState(false);
    const [bulkSelectedFields, setBulkSelectedFields] = useState({
        points: true,
        fromlist: true,
        lastname: false,
        firstname: false,
        nation: false,
        club: false,
        birthyear: false,
    });
    const [bulkRunning, setBulkRunning] = useState(false);
    const [bulkSummary, setBulkSummary] = useState(null);

    const runBulkUpdate = async () => {
        const fiscodes = athletes.map((a) => String(a.fiscode || "").trim()).filter(Boolean);
        if (fiscodes.length === 0) {
            setError("Aucun participant avec un numéro de licence (fiscode) renseigné.");
            return;
        }
        if (
            !window.confirm(
                `Mettre à jour ${fiscodes.length} participant(s) depuis la liste FIS importée, ` +
                `pour les champs sélectionnés ? Cette action écrase les valeurs actuelles.`
            )
        ) {
            return;
        }

        setBulkRunning(true);
        setBulkSummary(null);
        setError("");
        try {
            const res = await apiFetch("/api/timing/fis-pointslist/bulk-lookup", {
                method: "POST",
                body: JSON.stringify({ fiscodes }),
            });
            if (!res) return;
            const rows = await res.json().catch(() => []);
            if (!res.ok) {
                setError(formatApiError(rows, res));
                return;
            }
            const byFiscode = new Map(rows.map((r) => [String(r.fiscode), r]));

            let updatedCount = 0;
            let notFoundCount = 0;
            let noFiscodeCount = 0;

            const updated = athletes.map((a) => {
                const fiscode = String(a.fiscode || "").trim();
                if (!fiscode) {
                    noFiscodeCount++;
                    return a;
                }
                const match = byFiscode.get(fiscode);
                if (!match) {
                    notFoundCount++;
                    return a;
                }
                updatedCount++;
                const next = { ...a };
                if (bulkSelectedFields.fromlist) next.fromlist = match.list_id || a.fromlist;
                if (bulkSelectedFields.lastname) next.lastname = match.lastname;
                if (bulkSelectedFields.firstname) next.firstname = match.firstname;
                if (bulkSelectedFields.nation) next.nation = match.nation;
                if (bulkSelectedFields.club) next.club = match.club || "";
                if (bulkSelectedFields.birthyear) next.birthyear = match.birthyear;
                if (bulkSelectedFields.points) {
                    const p = match[pointsDiscipline];
                    next.points = p != null && p < 999 ? p : "";
                }
                return next;
            });

            const ok = await persistAthletes(updated);
            if (ok) {
                setBulkSummary({ updated: updatedCount, notFound: notFoundCount, noFiscode: noFiscodeCount });
                setStatus(
                    `Mise à jour groupée ✓ — ${updatedCount} mis à jour, ${notFoundCount} non trouvés dans la liste` +
                    (noFiscodeCount ? `, ${noFiscodeCount} sans fiscode (ignorés)` : "") +
                    "."
                );
            }
        } finally {
            setBulkRunning(false);
        }
    };

    // -----------------------------
    // Tri
    // -----------------------------
    function compareBySort(a, b) {
        if (sortBy === "points") {
            const pa = parseFloat(a.points);
            const pb = parseFloat(b.points);
            const va = isNaN(pa) ? Infinity : pa;
            const vb = isNaN(pb) ? Infinity : pb;
            return va - vb;
        }
        if (sortBy === "club") {
            return String(a.club || "").localeCompare(String(b.club || ""));
        }
        if (sortBy === "field" && sortField) {
            // Tri numérique si les deux valeurs sont des nombres (ex:
            // dossard, année), alphabétique sinon.
            const va = a[sortField];
            const vb = b[sortField];
            const na = parseFloat(va);
            const nb = parseFloat(vb);
            if (!isNaN(na) && !isNaN(nb) && va !== "" && vb !== "") return na - nb;
            return String(va ?? "").localeCompare(String(vb ?? ""));
        }
        // "abc" par défaut : nom puis prénom
        const lastCompare = String(a.lastname || "").localeCompare(String(b.lastname || ""));
        if (lastCompare !== 0) return lastCompare;
        return String(a.firstname || "").localeCompare(String(b.firstname || ""));
    }

    // Groupes pour l'affichage/impression : soit un seul groupe (pas de
    // regroupement), soit un groupe par valeur du champ choisi (trié
    // alphabétiquement par cette valeur), chaque groupe étant ensuite
    // trié selon le critère choisi (ABC/points/club/champ).
    // Participants correspondant à la recherche (nom, prénom, dossard, code,
    // club… : n'importe quel champ, sans tenir compte des accents/majuscules).
    const filteredAthletes = useMemo(() => {
        const norm = (v) => String(v ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
        const terms = norm(search).split(/\s+/).filter(Boolean);
        if (!terms.length) return athletes;
        return athletes.filter((a) => {
            const text = norm(Object.values(a).join(" "));
            return terms.every((t) => text.includes(t));
        });
    }, [athletes, search]);

    const groups = useMemo(() => {
        if (!groupByField) {
            return [{ category: null, entries: [...filteredAthletes].sort(compareBySort) }];
        }

        const byCategory = {};
        filteredAthletes.forEach((a) => {
            const cat = a[groupByField] || `(${COLUMN_LABELS[groupByField] || groupByField} vide)`;
            if (!byCategory[cat]) byCategory[cat] = [];
            byCategory[cat].push(a);
        });

        return Object.keys(byCategory)
            .sort((a, b) => a.localeCompare(b))
            .map((cat) => ({ category: cat, entries: byCategory[cat].sort(compareBySort) }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredAthletes, sortBy, sortField, groupByField]);

    // =====================================================================
    // PDF "Liste des participants" — ajouté le 09.09.2026, même style de
    // titre que les autres PDF mais SANS le bloc jury/météo/parcours,
    // volontairement omis ici — juste le titre puis le tableau.
    // =====================================================================
    const generateParticipantsPdf = async () => {
        if (athletes.length === 0) {
            setError("Aucun participant à imprimer.");
            return;
        }

        // Titre depuis la config Swiss-Ski si disponible — best-effort,
        // ne bloque pas la génération si absente/erreur.
        let raceTitle = eventKey;
        let disciplineLabel = "";
        try {
            const res = await apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`);
            if (res && res.ok) {
                const cfg = await res.json();
                raceTitle = cfg?.raceTitle || cfg?.event || eventKey;
                disciplineLabel = DISCIPLINE_LABELS[cfg?.discipline] || cfg?.discipline || "";
            }
        } catch {
            /* best-effort, titre générique si indisponible */
        }

        const doc = new jsPDF();
        doc.setFontSize(13);
        doc.setFont(undefined, "bold");
        doc.text(raceTitle, 105, 14, { align: "center" });
        doc.setFont(undefined, "normal");
        let y = 21;
        if (disciplineLabel) {
            doc.setFontSize(11);
            doc.text(disciplineLabel, 105, y, { align: "center" });
            y += 6;
        }
        doc.setFontSize(10);
        doc.text("Participants list", 105, y, { align: "center" });
        y += 8;

        const head = [displayColumns.map((c) => COLUMN_LABELS[c] || c)];
        const rowOf = (a) => displayColumns.map((c) => (a[c] ?? "").toString());

        if (groupByField) {
            const byCat = {};
            [...athletes].sort(compareBySort).forEach((a) => {
                const cat = a[groupByField] || `(${COLUMN_LABELS[groupByField] || groupByField} vide)`;
                (byCat[cat] = byCat[cat] || []).push(a);
            });
            const catKeys = Object.keys(byCat).sort((a, b) => a.localeCompare(b));
            catKeys.forEach((cat, idx) => {
                if (idx > 0) {
                    doc.addPage();
                    y = 14;
                }
                doc.setFontSize(12);
                doc.setFont(undefined, "bold");
                doc.text(cat, 14, y);
                doc.setFont(undefined, "normal");
                y += 6;
                autoTable(doc, {
                    startY: y,
                    margin: { left: 14, right: 14 },
                    styles: { fontSize: 8 },
                    headStyles: { fillColor: [20, 20, 20], textColor: 255 },
                    head,
                    body: byCat[cat].map(rowOf),
                });
                y = doc.lastAutoTable.finalY + 8;
            });
        } else {
            autoTable(doc, {
                startY: y,
                margin: { left: 14, right: 14 },
                styles: { fontSize: 8 },
                headStyles: { fillColor: [20, 20, 20], textColor: 255 },
                head,
                body: [...athletes].sort(compareBySort).map(rowOf),
            });
        }

        doc.save(`participants-${eventKey}.pdf`);
    };

    return (
        <Box p={3}>
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .athletes-category-group {
                        break-after: page;
                        page-break-after: always;
                    }
                    .athletes-category-group:last-child {
                        break-after: auto;
                        page-break-after: auto;
                    }
                }
            `}</style>

            <TimingEventSubNav eventKey={eventKey} current="athletes" />

            <Typography variant="h4" mb={1} className="no-print">
                Participants — {eventKey}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2} className="no-print">
                Édition manuelle possible directement dans le tableau — pense à enregistrer après
                modification.
            </Typography>

            {/* Listes de points : FIS (import en base + ajout/rafraîchissement)
                et Swiss-Ski (fichier lu directement, mise à jour groupée). */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }} className="no-print">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Liste de points FIS
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Discipline</InputLabel>
                        <Select label="Discipline" value={pointsDiscipline} onChange={(e) => setPointsDiscipline(e.target.value)}>
                            {FIS_DISCIPLINES.map((d) => (
                                <MenuItem key={d.value} value={d.value}>
                                    {d.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />} disabled={fisImporting}>
                        {fisImporting ? "Import en cours…" : "Importer liste de points FIS"}
                        <input type="file" accept=".txt,.tsv,.csv,.xlsx,.xls" hidden onChange={handleFisListFile} />
                    </Button>

                    <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => setShowAddDialog(true)}>
                        Ajouter un athlète (liste FIS / Swiss-Ski)
                    </Button>

                    <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => setShowBulkDialog(true)}>
                        Mettre à jour tous les participants (FIS)
                    </Button>

                    {fisListStatus?.count > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Liste FIS en base : {fisListStatus.count} athlètes
                            {fisListStatus.listId ? ` — n°${fisListStatus.listId}` : ""}
                            {fisListStatus.listName ? ` — ${fisListStatus.listName}` : ""}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Liste de points Swiss-Ski
                </Typography>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => setShowSwissSkiDialog(true)}>
                    Mettre à jour tous les participants (Swiss-Ski)
                </Button>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Séance des chefs d'équipe
                </Typography>
                <Button variant="outlined" size="small" startIcon={<GroupsIcon />} onClick={() => setShowTeamCheck(true)}>
                    Validation des inscriptions par les entraîneurs (lien WhatsApp)
                </Button>
            </Paper>

            <Box className="no-print" sx={{ display: "flex", gap: 2, alignItems: "center", mb: 3, flexWrap: "wrap" }}>
                <ToggleButtonGroup
                    value={sortBy}
                    exclusive
                    onChange={(e, v) => v && setSortBy(v)}
                    size="small"
                >
                    <ToggleButton value="abc">Tri ABC</ToggleButton>
                    <ToggleButton value="points">Tri par points</ToggleButton>
                    <ToggleButton value="club">Tri par club</ToggleButton>
                    <ToggleButton value="field">Tri par champ...</ToggleButton>
                </ToggleButtonGroup>

                {sortBy === "field" && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Champ</InputLabel>
                        <Select label="Champ" value={sortField} onChange={(e) => setSortField(e.target.value)}>
                            {columns.map((c) => (
                                <MenuItem key={c} value={c}>
                                    {COLUMN_LABELS[c] || c}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Regrouper / saut de page par</InputLabel>
                    <Select
                        label="Regrouper / saut de page par"
                        value={groupByField}
                        onChange={(e) => setGroupByField(e.target.value)}
                    >
                        <MenuItem value="">Aucun</MenuItem>
                        {columns.map((c) => (
                            <MenuItem key={c} value={c}>
                                {COLUMN_LABELS[c] || c}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button variant="outlined" onClick={() => setShowFieldTools(true)}>
                    Outils champs (Aa → AA, copier)
                </Button>

                <Button variant="outlined" onClick={() => setShowColumnsDialog(true)}>
                    Colonnes ({displayColumns.length}/{columns.length})
                </Button>

                <Button variant="outlined" onClick={generateParticipantsPdf}>
                    PDF (design officiel)
                </Button>

                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={deleteAllAthletes}
                    disabled={athletes.length === 0}
                >
                    Supprimer tous les participants
                </Button>
            </Box>

            <Box className="no-print" sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                <TextField
                    size="small"
                    placeholder="Rechercher (nom, dossard, code, club…)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 340 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                {search && (
                    <Typography variant="body2" color="text.secondary">
                        {filteredAthletes.length} / {athletes.length} participant(s)
                        <Button size="small" onClick={() => setSearch("")} sx={{ ml: 1 }}>
                            Effacer
                        </Button>
                    </Typography>
                )}
            </Box>

            {error && (
                <Typography color="error" className="no-print" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}
            {status && (
                <Typography color="success.main" className="no-print" sx={{ mb: 2 }}>
                    {status}
                </Typography>
            )}

            {athletes.length === 0 ? (
                <Typography color="text.secondary" className="no-print">
                    Aucun participant importé pour cet event.
                </Typography>
            ) : (
                groups.map((group, gi) => (
                    <Box key={gi} className="athletes-category-group" sx={{ mb: 4 }}>
                        {group.category && (
                            <Typography variant="h6" sx={{ mb: 1, mt: gi > 0 ? 2 : 0 }}>
                                {group.category}
                            </Typography>
                        )}

                        <Paper variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {displayColumns.map((col) => (
                                            <TableCell key={col} sx={{ fontWeight: "bold" }}>
                                                {COLUMN_LABELS[col] || col}
                                            </TableCell>
                                        ))}
                                        <TableCell className="no-print" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {group.entries.map((a) => {
                                        // Retrouve l'index réel dans `athletes` (pas dans le
                                        // groupe trié) pour que l'édition modifie la bonne
                                        // entrée.
                                        const realIndex = athletes.indexOf(a);
                                        return (
                                            <TableRow key={realIndex}>
                                                {displayColumns.map((col) => (
                                                    <TableCell key={col}>{a[col]}</TableCell>
                                                ))}
                                                <TableCell className="no-print" sx={{ whiteSpace: "nowrap" }}>
                                                    <IconButton size="small" onClick={() => openEditor(realIndex)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small" color="error" onClick={() => deleteAthlete(realIndex)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Paper>
                    </Box>
                ))
            )}

            {/* Fenêtre d'édition d'un seul concurrent */}
            <Dialog open={editingIndex !== null} onClose={cancelEditor} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Modifier {draft?.lastname} {draft?.firstname}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                        <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={refreshPointsInEditor}
                            disabled={refreshingPoints || !draft?.fiscode}
                        >
                            {refreshingPoints ? "Recherche…" : "Rafraîchir depuis la liste FIS"}
                        </Button>
                    </Box>
                    {refreshError && (
                        <Typography color="error" variant="body2" sx={{ mb: 1 }}>
                            {refreshError}
                        </Typography>
                    )}
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {draft &&
                            columns.map((col) => (
                                <Grid item xs={12} sm={6} key={col}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={COLUMN_LABELS[col] || col}
                                        value={draft[col] ?? ""}
                                        onChange={(e) => updateDraftField(col, e.target.value)}
                                    />
                                </Grid>
                            ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelEditor} disabled={saving}>
                        Annuler
                    </Button>
                    <Button variant="contained" onClick={saveEditor} disabled={saving}>
                        {saving ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Fenêtre d'ajout d'un athlète depuis la liste de points FIS */}
            <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Ajouter un athlète depuis une liste de points</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1, flexWrap: "wrap" }}>
                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={addSource}
                            onChange={(_, v) => v && (setAddSource(v), setAddSearchResults([]))}
                        >
                            <ToggleButton value="fis">FIS{raceSector ? (raceSector === "TM" ? " télémark" : " alpin") : ""}</ToggleButton>
                            <ToggleButton value="swissski">Swiss-Ski</ToggleButton>
                        </ToggleButtonGroup>
                        {addSource === "swissski" && (
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                                <InputLabel>Points</InputLabel>
                                <Select label="Points" value={swissPointsCol} onChange={(e) => setSwissPointsCol(e.target.value)}>
                                    {["Punkte", "Punkte1", "Punkte2", "Punkte3", "Punkte4", "Punkte5"].map((c) => (
                                        <MenuItem key={c} value={c}>
                                            {c}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={addSource === "swissski" ? "Nom, prénom, n° Swiss-Ski ou code FIS" : "Nom, prénom ou fiscode"}
                            value={addSearchQuery}
                            onChange={(e) => setAddSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") searchFisAthletes();
                            }}
                        />
                        <Button variant="contained" onClick={searchFisAthletes} disabled={addSearching}>
                            {addSearching ? "…" : "Chercher"}
                        </Button>
                    </Box>

                    {addSearchResults.length === 0 ? (
                        <Typography color="text.secondary" variant="body2">
                            {addSource === "swissski"
                                ? "Aucun résultat pour l'instant (la liste Swiss-Ski en base est celle importée via « Mettre à jour depuis Swiss-Ski »)."
                                : fisListStatus?.count > 0
                                    ? "Aucun résultat pour l'instant."
                                    : "Aucune liste de points FIS importée — importe-la d'abord via le bouton ci-dessus."}
                        </Typography>
                    ) : addSource === "swissski" ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nom</TableCell>
                                    <TableCell>Prénom</TableCell>
                                    <TableCell>Année</TableCell>
                                    <TableCell>Club</TableCell>
                                    <TableCell align="right">{swissPointsCol}</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {addSearchResults.map((r) => (
                                    <TableRow key={r.PNr}>
                                        <TableCell>{r.Nachname}</TableCell>
                                        <TableCell>{r.Vorname}</TableCell>
                                        <TableCell>{r.JG}</TableCell>
                                        <TableCell>{r.Clubname}</TableCell>
                                        <TableCell align="right">{r[swissPointsCol] || "—"}</TableCell>
                                        <TableCell>
                                            <Button size="small" onClick={() => importAthleteFromSwissSki(r)}>
                                                Importer
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nom</TableCell>
                                    <TableCell>Prénom</TableCell>
                                    <TableCell>Nation</TableCell>
                                    <TableCell align="right">{FIS_DISCIPLINES.find((d) => d.value === pointsDiscipline)?.label} pts</TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {addSearchResults.map((r) => (
                                    <TableRow key={r.fiscode}>
                                        <TableCell>{r.lastname}</TableCell>
                                        <TableCell>{r.firstname}</TableCell>
                                        <TableCell>{r.nation}</TableCell>
                                        <TableCell align="right">
                                            {r[pointsDiscipline] != null && r[pointsDiscipline] < 999 ? r[pointsDiscipline] : "—"}
                                        </TableCell>
                                        <TableCell>
                                            <Button size="small" onClick={() => importAthleteFromFis(r)}>
                                                Importer
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAddDialog(false)}>Fermer</Button>
                </DialogActions>
            </Dialog>

            {/* Fenêtre de mise à jour groupée de tous les participants (FIS) */}
            <Dialog open={showBulkDialog} onClose={() => setShowBulkDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Mettre à jour tous les participants (liste FIS)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Rapproche chaque participant de la liste FIS importée par son numéro de licence
                        (fiscode), et écrase les champs cochés ci-dessous avec les valeurs trouvées.
                        Discipline utilisée pour les points : {FIS_DISCIPLINES.find((d) => d.value === pointsDiscipline)?.label}
                        {" "}(modifiable dans la barre au-dessus).
                    </Typography>

                    <Divider sx={{ mb: 1 }} />
                    <FormControlLabel
                        sx={{ display: "block" }}
                        control={
                            <Checkbox
                                checked={BULK_FIELDS.every((f) => bulkSelectedFields[f.key])}
                                indeterminate={
                                    BULK_FIELDS.some((f) => bulkSelectedFields[f.key]) &&
                                    !BULK_FIELDS.every((f) => bulkSelectedFields[f.key])
                                }
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBulkSelectedFields(
                                        Object.fromEntries(BULK_FIELDS.map((f) => [f.key, checked]))
                                    );
                                }}
                            />
                        }
                        label={<b>Tout sélectionner</b>}
                    />
                    <Divider sx={{ mb: 1 }} />

                    {BULK_FIELDS.map((f) => (
                        <FormControlLabel
                            key={f.key}
                            sx={{ display: "block" }}
                            control={
                                <Checkbox
                                    checked={bulkSelectedFields[f.key]}
                                    onChange={(e) =>
                                        setBulkSelectedFields((prev) => ({ ...prev, [f.key]: e.target.checked }))
                                    }
                                />
                            }
                            label={f.label}
                        />
                    ))}

                    {bulkSummary && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            Dernier résultat : <b>{bulkSummary.updated}</b> mis à jour, <b>{bulkSummary.notFound}</b> non
                            trouvés dans la liste, <b>{bulkSummary.noFiscode}</b> sans fiscode.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowBulkDialog(false)}>Fermer</Button>
                    <Button
                        variant="contained"
                        onClick={runBulkUpdate}
                        disabled={bulkRunning || !Object.values(bulkSelectedFields).some(Boolean)}
                    >
                        {bulkRunning ? "Mise à jour en cours…" : "Lancer la mise à jour"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Fenêtre de mise à jour depuis le fichier Swiss-Ski */}
            <SwissSkiUpdateDialog
                open={showSwissSkiDialog}
                onClose={() => setShowSwissSkiDialog(false)}
                athletes={athletes}
                persistAthletes={persistAthletes}
                onDone={(message) => {
                    setError("");
                    setStatus(message);
                }}
            />

            <TeamCheckAdmin open={showTeamCheck} onClose={() => setShowTeamCheck(false)} eventKey={eventKey} onChanged={load} />

            {/* Outils de champs : casse et copie */}
            <Dialog open={showFieldTools} onClose={() => setShowFieldTools(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Outils de champs</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Changer la casse d'un champ (tous les participants)</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <FormControl size="small" sx={{ minWidth: 170 }}>
                            <InputLabel>Champ</InputLabel>
                            <Select label="Champ" value={ftField} onChange={(e) => setFtField(e.target.value)}>
                                {columns.map((c) => (
                                    <MenuItem key={c} value={c}>{COLUMN_LABELS[c] || c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <ToggleButtonGroup size="small" exclusive value={ftCase} onChange={(_, v) => v && setFtCase(v)}>
                            <ToggleButton value="upper">MAJUSCULES</ToggleButton>
                            <ToggleButton value="title">Initiale</ToggleButton>
                            <ToggleButton value="lower">minuscules</ToggleButton>
                        </ToggleButtonGroup>
                        <Button variant="contained" onClick={applyCase}>Appliquer</Button>
                    </Box>
                    {athletes[0] && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            Exemple : « {String(athletes[0][ftField] ?? "")} » →{" "}
                            « {athletes[0][ftField] == null ? "" : ftCase === "upper" ? String(athletes[0][ftField]).toLocaleUpperCase("fr-CH") : ftCase === "lower" ? String(athletes[0][ftField]).toLocaleLowerCase("fr-CH") : titleCase(athletes[0][ftField])} »
                        </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Copier un champ dans un autre</Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>De</InputLabel>
                            <Select label="De" value={ftFrom} onChange={(e) => setFtFrom(e.target.value)}>
                                {columns.map((c) => (
                                    <MenuItem key={c} value={c}>{COLUMN_LABELS[c] || c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography>→</Typography>
                        <TextField
                            size="small"
                            label="Vers (champ existant ou nouveau)"
                            value={ftTo}
                            onChange={(e) => setFtTo(e.target.value)}
                            inputProps={{ list: "ft-fields" }}
                            sx={{ minWidth: 220 }}
                        />
                        <datalist id="ft-fields">
                            {columns.map((c) => (
                                <option key={c} value={c}>{COLUMN_LABELS[c] || c}</option>
                            ))}
                        </datalist>
                    </Box>
                    <FormControlLabel
                        control={<Checkbox checked={ftOnlyEmpty} onChange={(e) => setFtOnlyEmpty(e.target.checked)} />}
                        label="Seulement si le champ cible est vide"
                    />
                    <Box>
                        <Button variant="contained" onClick={applyCopy}>Copier</Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowFieldTools(false)}>Fermer</Button>
                </DialogActions>
            </Dialog>

            {/* Fenêtre de sélection des colonnes affichées/imprimées/PDF */}
            <Dialog open={showColumnsDialog} onClose={() => setShowColumnsDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Colonnes à afficher / imprimer</DialogTitle>
                <DialogContent>
                    <FormControlLabel
                        sx={{ display: "block" }}
                        control={
                            <Checkbox
                                checked={displayColumns.length === columns.length}
                                indeterminate={displayColumns.length > 0 && displayColumns.length < columns.length}
                                onChange={(e) => setPrintColumns(e.target.checked ? columns : [])}
                            />
                        }
                        label={<b>Tout sélectionner</b>}
                    />
                    <Divider sx={{ mb: 1 }} />
                    {columns.map((c) => (
                        <FormControlLabel
                            key={c}
                            sx={{ display: "block" }}
                            control={
                                <Checkbox
                                    checked={(printColumns || columns).includes(c)}
                                    onChange={(e) =>
                                        setPrintColumns((prev) => {
                                            const base = prev || columns;
                                            return e.target.checked
                                                ? [...base, c].filter((v, i, arr) => arr.indexOf(v) === i)
                                                : base.filter((v) => v !== c);
                                        })
                                    }
                                />
                            }
                            label={COLUMN_LABELS[c] || c}
                        />
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowColumnsDialog(false)}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}